import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import keycloak from "../../lib/keycloak";
import { completeSsoLogin, verifyStoredSession } from "../../lib/profileEngine";
import { isEmbedded, launchToken } from "../../lib/embedded";

const CALLBACK_PATH = "/auth/callback";
const RETURN_TO_KEY = "spiritual-profile-platform-sso-return-to";
const DEFAULT_LANDING = "/profile/ssp";

const REDIRECT_URI = process.env.REACT_APP_KEYCLOAK_REDIRECT_URI || `${window.location.origin}${CALLBACK_PATH}`;

const rememberDestination = () => {
  const { pathname, search, hash } = window.location;
  if (pathname === CALLBACK_PATH) return;
  try {
    window.sessionStorage.setItem(RETURN_TO_KEY, `${pathname}${search}${hash}`);
  } catch {
    // Without storage the user simply lands on the default page after login.
  }
};

const takeDestination = () => {
  try {
    const value = window.sessionStorage.getItem(RETURN_TO_KEY);
    window.sessionStorage.removeItem(RETURN_TO_KEY);
    if (value && value !== "/" && !value.startsWith(CALLBACK_PATH)) return value;
  } catch {
    // fall through to the default
  }
  return DEFAULT_LANDING;
};

let keycloakInit = null;

const initKeycloakOnce = () => {
  if (!keycloakInit) {
    rememberDestination();
    keycloakInit = keycloak.init({
      onLoad: "login-required",
      pkceMethod: "S256",
      checkLoginIframe: false,
      redirectUri: REDIRECT_URI,
    });
  }
  return keycloakInit;
};

const FullScreenNotice = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center text-ink px-6 text-center">
    <div className="font-mono text-[16px] tracking-widest-2 max-w-md leading-relaxed">{children}</div>
  </div>
);

// Mobile app hand-over: the app passes the learner's Keycloak access token; SPP verifies it exactly like a
// browser login (POST /api/auth/session) and starts its own session. One attempt per page load.
let launchLogin = null;
const launchLoginOnce = (token) => {
  if (!launchLogin) launchLogin = completeSsoLogin(token);
  return launchLogin;
};

const SsoGate = ({ children }) => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const [status, setStatus] = useState("initializing");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    const blockedOrLogin = (err) => {
      if (cancelled) return;
      if (err && err.status === 403) {
        setMessage(err.message || "Unable to sign you in.");
        setStatus("blocked");
      } else {
        keycloak.login({ redirectUri: REDIRECT_URI });
      }
    };

    const browserLogin = () =>
      initKeycloakOnce()
        .then(async (authenticated) => {
          if (cancelled) return;
          if (!authenticated) {
            keycloak.login({ redirectUri: REDIRECT_URI });
            return;
          }
          try {
            await completeSsoLogin(keycloak.token);
            if (cancelled) return;
            if (window.location.pathname === CALLBACK_PATH) {
              navigateRef.current(takeDestination(), { replace: true });
            }
            setStatus("ready");
          } catch (err) {
            blockedOrLogin(err);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setMessage("Unable to reach the sign-in service. Please try again.");
            setStatus("blocked");
          }
        });

    if (launchToken) {
      // Opened from the Redacamy mobile app: sign in with the token it passed. If SPP rejects it (expired,
      // wrong client), fall back to the normal Redacamy login page.
      launchLoginOnce(launchToken)
        .then(() => {
          if (!cancelled) setStatus("ready");
        })
        .catch((err) => {
          launchLogin = null;
          if (cancelled) return;
          if (err && err.status === 403) blockedOrLogin(err);
          else browserLogin();
        });
    } else if (isEmbedded()) {
      // Another page load inside the app: keep the session SPP already started for this tab.
      verifyStoredSession().then((valid) => {
        if (cancelled) return;
        if (valid) setStatus("ready");
        else browserLogin();
      });
    } else {
      browserLogin();
    }

    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "initializing") {
    return <FullScreenNotice>◇ SIGNING YOU IN…</FullScreenNotice>;
  }
  if (status === "blocked") {
    return <FullScreenNotice>{message}</FullScreenNotice>;
  }
  return children;
};

export default SsoGate;
